import { DynamicModal } from "src/components/base/components/DynamicModal";
import { createState } from "src/utils/create_zustand_store";
import { Box, darken, lighten, Stack } from "@mui/material";
import { pluralize, throttleTime } from "src/utils/utils";
import { TCartPass, TPass } from "src/shared/types/globalStotre.types";
import { useGlobalStore } from "../../reusable/hooks/useGlobalStore";
import { SSText } from "src/components/base/components/SSText";
import React from "react";
import { useSSTheme } from "../../reusable/hooks/useSSTheme";
import Image from "next/image";
import { useTeamConfig } from "../../reusable/components/TeamConfigProvider";
import { useCssMediaQuery } from "src/components/base/hooks/useCssMediaQuery";

const useModalState = createState({
  isOpen: false,
  cartPasses: [] as TCartPass[],
  resolve: (() => {}) as Function,
});

export const useManageCartErrorModal = () => {
  const modalState = useModalState();

  const execute = async (state: Partial<typeof modalState>) => {
    modalState.set({ ...state, isOpen: true });
    return new Promise((resolve) => {
      modalState.set({ resolve });
    }) as Promise<{ isConfirmed: boolean }>;
  };

  return { ...modalState, execute };
};

export function ManageCartErrorModal() {
  const { md: mdUp } = useCssMediaQuery("up");
  const modalState = useManageCartErrorModal();
  const { passes } = useGlobalStore();
  // Guarded: callers pass `cartRes.passes` straight through, and an explicit
  // `undefined` overwrites the `[]` initial state on the shallow store merge.
  // An unhandled throw here takes the whole checkout page down.
  const hasAnySuccessPass = Boolean(
    modalState?.cartPasses?.some((itm) => itm?.isHeld)
  );

  const onClickCheckout = () => {
    modalState.set({ isOpen: false });
    modalState.resolve({ isConfirmed: true });
    throttleTime(200, () => modalState.reset());
    // make sure UI is scrolled to the bottom
    const scrollHeight = document.body.scrollHeight - 1000; // substracted footer height
    window.scrollTo({ top: scrollHeight, behavior: "smooth" });
  };

  const onClickTryAgain = () => {
    modalState.set({ isOpen: false });
    modalState.resolve({ isConfirmed: false });
    throttleTime(200, () => modalState.reset());
    // make sure UI is scrolled to the top
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <DynamicModal
      hideTitleIcon
      isOpen={modalState.isOpen}
      title={"Checkout Error"}
      onClose={onClickTryAgain}
      renderBody={() => {
        return (
          <Box p={mdUp ? 4 : 2}>
            {hasAnySuccessPass ? (
              <Box textAlign={"center"} pb={2}>
                <SSText>
                  Some of the items in your cart are not available. You can try
                  again or continue to checkout with remaining items
                </SSText>
              </Box>
            ) : null}

            <Stack spacing={2.5}>
              {modalState?.cartPasses?.map((cartPass) => {
                const pass = passes.find((p) => p.passId == cartPass.passId);
                if (!pass) return null;
                const isFailed = !cartPass.isHeld;
                if (isFailed) pass.errorMessage = cartPass.errorMessage;
                pass.seatCount = cartPass.seatCount;
                return (
                  <ErrorModalPass
                    key={pass.passId}
                    pass={pass}
                    isFailed={!cartPass?.isHeld}
                  />
                );
              })}
            </Stack>
          </Box>
        );
      }}
      slot={{
        footer: {
          style: {
            display: "flex",
            flexDirection: "row",
            width: mdUp ? "60%" : "100%",
            alignSelf: "flex-end",
          },
        },
        primaryButton: {
          label: "Checkout",
          isHidden: !hasAnySuccessPass,
          onClick: onClickCheckout,
          style: { flexGrow: 1 },
        },
        secondaryButton: {
          variant: hasAnySuccessPass ? "outlined" : "contained",
          label: "Try Again",
          onClick: onClickTryAgain,
          style: { flexGrow: hasAnySuccessPass ? 1 : "unset" },
        },
      }}
    />
  );
}

function ErrorModalPass(props: { isFailed?: boolean; pass: TPass }) {
  const theme = useSSTheme();
  const { IMAGES, TEAM_FULL_NAME } = useTeamConfig();

  return (
    <Box
      p={2}
      border={`1px solid  ${theme.colors.border}`}
      borderRadius={1.8}
      my={1}
    >
      <Stack direction={"row"} spacing={1} alignItems={"center"}>
        <Stack
          minWidth={"40px"}
          minHeight={"40px"}
          justifyContent={"center"}
          alignItems={"center"}
          borderRadius={1.5}
          overflow={"hidden"}
        >
          <Image
            alt={`${TEAM_FULL_NAME} logo`}
            src={IMAGES.LOGO_URL}
            width={40}
            height={40}
            style={{ objectFit: "cover" }}
          />
        </Stack>

        <Stack width={"100%"} spacing={0.3}>
          <Stack
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Box flexGrow={1}>
              <SSText
                variant="body"
                fontWeight={700}
                lineHeight={1.1}
                fontSize={"0.9rem"}
                dangerouslySetInnerHTML={{ __html: props?.pass?.passTitle }}
              />
            </Box>
            <Box
              py={"4px"}
              px={"8px"}
              borderRadius={1}
              border={`1px solid ${theme.colors.border}`}
            >
              <SSText
                variant="body"
                fontWeight={500}
                lineHeight={1.1}
                fontSize={"0.9rem"}
                color={"text.primary"}
                sx={{ whiteSpace: "nowrap" }}
              >
                {props?.pass?.seatCount}{" "}
                {pluralize("Seat", props?.pass?.seatCount)}
              </SSText>
            </Box>
          </Stack>
          <Box>
            {props?.isFailed ? (
              <SSText
                variant="body"
                color={"error.main"}
                lineHeight={1}
                fontSize={"0.85rem"}
              >
                {props?.pass?.errorMessage}
              </SSText>
            ) : (
              <Box
                py={"2px"}
                px={"6px"}
                bgcolor={lighten(theme.mui.palette.success.main, 0.9)}
                color={darken(theme.mui.palette.success.main, 0.5)}
                borderRadius={1}
                width={"fit-content"}
                fontSize={"0.85rem"}
              >
                Success
              </Box>
            )}
          </Box>
        </Stack>
      </Stack>
    </Box>
  );
}
